import { useState } from 'react';
import { saveEstimate } from '../../api/estimatesApi';

const SERVICE_LABELS: Record<string, string> = {
  'blob-storage': 'Blob Storage',
  'logic-apps': 'Logic Apps',
  'functions': 'Azure Functions',
  'app-service': 'App Service',
  'bandwidth': 'Bandwidth',
  'log-analytics': 'Log Analytics',
  'front-door': 'Azure Front Door',
  'firewall': 'Azure Firewall',
  'virtual-desktop': 'Azure Virtual Desktop',
};

interface ShareButtonProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputs: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any;
  service: string;
}

export function ShareButton({ inputs, result, service }: ShareButtonProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [copiedUrl, setCopiedUrl] = useState('');

  const handleShare = async () => {
    setStatus('loading');
    try {
      if (result) {
        const { url } = await saveEstimate(service, inputs, result);
        await navigator.clipboard.writeText(url);
        setCopiedUrl(url);
        // Save to localStorage
        const monthly = (result as Record<string, unknown>).monthly_estimate;
        const label = `${SERVICE_LABELS[service] ?? service} — £${typeof monthly === 'number' ? monthly.toFixed(2) : '?'}/mo`;
        try {
          const saved = JSON.parse(localStorage.getItem('azurecalc_saved_estimates') ?? '[]');
          const id = url.split('/').pop() ?? '';
          saved.unshift({ id, label, service, savedAt: new Date().toISOString(), estimateValue: label });
          localStorage.setItem('azurecalc_saved_estimates', JSON.stringify(saved.slice(0, 50)));
        } catch {
          // Private browsing or storage quota exceeded — non-fatal
        }
      } else {
        // No result yet — copy current page URL as fallback
        await navigator.clipboard.writeText(window.location.href);
        setCopiedUrl(window.location.href);
      }
      setStatus('success');
      setTimeout(() => setStatus('idle'), 4000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  const getButtonStyles = () => {
    if (status === 'success') return 'border-[#107c10] text-[#107c10]';
    if (status === 'error') return 'border-[#a4262c] text-[#a4262c]';
    return '';
  };

  const getButtonText = () => {
    if (status === 'loading') return 'Saving…';
    if (status === 'success') return `Link copied!`;
    if (status === 'error') return 'Could not save — try again';
    return result ? 'Save & Share Estimate' : 'Copy shareable link';
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={handleShare}
        disabled={status === 'loading'}
        className={`
          w-full flex items-center justify-center gap-2 rounded-sm border border-outline-variant
          bg-surface-container-high py-2.5 text-xs font-bold uppercase tracking-widest text-on-surface
          hover:bg-surface-container-highest transition-colors group disabled:opacity-60 disabled:cursor-not-allowed
          ${getButtonStyles()}
        `}
      >
        {status === 'loading' ? (
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="15 45" />
          </svg>
        ) : status === 'success' ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-azure-muted group-hover:text-azure-text-2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
        )}
        <span>{getButtonText()}</span>
      </button>
      {status === 'success' && copiedUrl && (
        <p className="text-[10px] text-[#107c10] text-center truncate px-1">{copiedUrl}</p>
      )}
    </div>
  );
}
